import {Inject, Injectable, OnDestroy} from '@angular/core';

import {of, ReplaySubject} from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  last,
  map,
  mergeMap,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';

import * as UU from '@common/utils/dist/utils/user-utils-pl2.js';
import {UtilsPL2 as U} from '@common/utils/dist/utils/utils-pl2.js';

import {APIError} from '@utils/api-errors';

import {RouteStore} from '@stores/route.store';
import {UserStore} from '@stores/user.store';
import {EventTrackingStore} from '@stores/event-tracking.store';
import {userChangeFilter} from '@utils/data-view-pipes';
import {
  EventAPIService,
  EventAPIServiceToken,
  UserEventInputBuilder,
} from '@services/event-api.service';
import {AuthApiService} from '@services/auth-api.service';
import {UserDetailsReadEventInputBuilder} from '@services/events/user-details-events';
import {PipeUtils as PU} from '@utils/pipe-utils';

@Injectable({
  providedIn: 'root',
})
export class UserReactor implements OnDestroy {
  private _destroyed$ = new ReplaySubject<boolean>();

  constructor(
    @Inject(EventAPIServiceToken) private eventAPIService: EventAPIService,
    private routeStore: RouteStore,
    private userStore: UserStore,
    private authApiService: AuthApiService,
    @Inject('SESSIONSTORAGE')
    private sessionStorage: Storage,
    private eventTrackingStore: EventTrackingStore,
  ) {
    this.routeStore.state$
      .pipe(
        map((state) => state.utm_source),
        distinctUntilChanged(),
        filter((utmCode) => !U.isEmpty(utmCode)),
        tap((utmCode) => this.sessionStorage.setItem('utmCode', utmCode)),
        takeUntil(this._destroyed$),
      )
      .subscribe();

    this.userStore.state$
      .pipe(
        userChangeFilter(),
        filter((user) => !U.isEmpty(user) && !UU.isUnconfirmed(user)),
        mergeMap((user) => {
          const utmCode = this.sessionStorage.getItem('utmCode');
          const event = new UserEventInputBuilder(user, utmCode);
          return this.eventAPIService.sendEvent(event).pipe(
            last(),
            mergeMap(() => {
              if (!U.isEmpty(user.init)) {
                return this.userStore.deleteUserAttribute('custom:init');
              } else {
                return of(void 0);
              }
            }),
            catchError((err) => {
              console.warn(err);
              return of(APIError.buildError(err));
            }),
          );
        }),
        takeUntil(this._destroyed$),
      )
      .subscribe();

    this.userStore.state$
      .pipe(
        userChangeFilter(),
        filter((user) => !U.isEmpty(user) && !UU.isUnconfirmed(user)),
        take(1),
        mergeMap(() =>
          this.authApiService.requestSignedCookies().pipe(
            catchError((err) => {
              console.warn('Failed to request signed cookies:', err);
              return of(void 0);
            }),
          ),
        ),
        takeUntil(this._destroyed$),
      )
      .subscribe();

    this.userStore.state$
      .pipe(
        userChangeFilter(),
        filter((user) => !U.isEmpty(user)),
        take(1),
        filter((user) => UU.isUnconfirmed(user)),
        takeUntil(this._destroyed$),
      )
      .subscribe(() => {
        routeStore.url('/auth/role-confirm');
      });

    this.userStore.state$
      .pipe(
        userChangeFilter(),
        filter((user) => !U.isEmpty(user) && !UU.isUnconfirmed(user)),
        take(1),
        switchMap(() =>
          this.eventAPIService
            .sendEvent(new UserDetailsReadEventInputBuilder())
            .pipe(PU.waitForSuccess(this.eventTrackingStore)),
        ),
        catchError(() => of(void 0)), // TODO should we retry for some error?
        takeUntil(this._destroyed$),
      )
      .subscribe();
  }

  ngOnDestroy() {
    this._destroyed$.next(true);
    this._destroyed$.complete();
  }
}
